/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { simpleFetch } from "./restWrapper";
import { DEFAULT_LANGUAGE, DONOR_AUTH, ADMIN_AUTH, ORG_AUTH, ORG_KIND } from "./config";
import { Translation } from "react-i18next";
import { checkLoginStatus, getOrganizationID } from "./assets/helper.js";

const AuthContext = React.createContext();
const AuthConsumer = AuthContext.Consumer;

// Determine the application platform from environment variables
const appPlatform = process.env.REACT_APP_PLATFORM || "checkplus"; // Default to checkplus

class AuthProvider extends Component {
  constructor() {
    super();
    this.login = this.login.bind(this);
    this.orgLogin = this.orgLogin.bind(this);
    this.adminLogin = this.adminLogin.bind(this);
    this.marketplaceLogin = this.marketplaceLogin.bind(this);
    this.forgetSession = this.forgetSession.bind(this);

    this.logout = this.logout.bind(this);
    this.deleteAccount = this.deleteAccount.bind(this);

    this.getOrgLoginStatus = this.getOrgLoginStatus.bind(this);
    
  }
  
  componentDidMount() {}
  
  login(credentials) {
    const parsedDomain = getOrganizationID(window.location.href);
    return simpleFetch("/donors/login", "POST", credentials, false, {
      isCustomDomain: parsedDomain.isCustomDomain
    }).then((res) => {
      if (![200, 204].includes(res.status)) {
        this.setLoginStatus(false);
        return res;
      }
      if (res.status === 200) {
        localStorage.setItem("accessToken", res.body.accessToken);
        localStorage.setItem("expiredIn", res.body.expiredIn);
      }
      return simpleFetch("/donors/status", "GET", {}, false, {
        isCustomDomain: parsedDomain.isCustomDomain,
      }).then((res) => {
        if (res.status !== 204) {
          this.setLoginStatus(false);
          return res;
        }
        this.setLoginStatus(true);
        this.setOrgLoginStatus(false);
        this.setAdminStatus(false);
        return res;
      });
    });
  }

  adminLogin(credentials) {
    // Staff sign in on the cookie session. A leftover donor/marketplace
    // accessToken makes simpleFetch send this as an external (bearer)
    // request, which the backend answers with 200 + a token instead of a
    // 204 session — so a correct password would read as a failure.
    localStorage.removeItem("accessToken");
    localStorage.removeItem("expiredIn");
    return simpleFetch("/donors/login", "POST", credentials).then((res) => {
      if (res.status !== 204) {
        this.setAdminStatus(false);
        return res;
      }
      this.setAdminStatus(true);
      this.setLoginStatus(false);
      this.setOrgLoginStatus(false);
      return res;
    });
  }

  orgLogin(credentials) {
    // Same as adminLogin: orgs sign in on the cookie session, so a leftover
    // accessToken must not turn this into a bearer request answered with 200.
    localStorage.removeItem("accessToken");
    localStorage.removeItem("expiredIn");
    return simpleFetch("/donors/login", "POST", credentials).then((res) => {
      if (res.status !== 204) {
        this.setOrgLoginStatus(false);
        return res;
      }
      return simpleFetch("/organizations/status", "GET").then((res) => {
        if (res.status !== 204) {
          this.setOrgLoginStatus(false);
          return res;
        }
        this.setOrgLoginStatus(true);
        this.setLoginStatus(false);
        this.setAdminStatus(false);
        return res;
      });
    });
  }

  /**
   * Plug Marketplace has one sign-in form for every account type, so the kind
   * of account is discovered rather than chosen. POST /donors/login
   * authenticates donors and organizations alike; the two status endpoints
   * then disambiguate — verifyDonorOnly answers 403 for an organization
   * session, and verifyOrganizationOnly answers 403 for a donor one.
   *
   * Resolves to { status, body, kind } where kind is "donor", "nonprofit",
   * "business", or null when the credentials were rejected.
   */
  marketplaceLogin(credentials) {
    const parsedDomain = getOrganizationID(window.location.href);
    const failed = (res) => {
      this.setLoginStatus(false);
      this.setOrgLoginStatus(false);
      return { status: res.status, body: res.body || {}, kind: null };
    };
    return simpleFetch("/donors/login", "POST", credentials, false, {
      isCustomDomain: parsedDomain.isCustomDomain,
    }).then((res) => {
      if (![200, 204].includes(res.status)) {
        return failed(res);
      }
      if (res.status === 200 && res.body) {
        localStorage.setItem("accessToken", res.body.accessToken);
        localStorage.setItem("expiredIn", res.body.expiredIn);
      }
      // POST /login reports the organisation type, but only when it answers
      // 200. A cookie sign-in from the marketplace is not an "external"
      // request, so it gets a bodyless 204 and this is undefined — which is
      // why the probes below, not this, decide the answer.
      const loginOrgType = res.body && res.body.organizationType;

      // The three probes do not depend on each other, so they go together.
      // Run in sequence they made an organisation sign-in four round trips
      // deep, each one paying for a session read and a rate-limiter write
      // before it reached a handler, with the button sitting on "Signing in…"
      // for all of it.
      //
      // Exactly one of the first two can succeed: verifyDonorOnly answers 403
      // for an organisation session and verifyOrganizationOnly answers 403 for
      // a donor one. The third is only read when the second wins, and costs a
      // donor one wasted 403 — which simpleFetch ignores, since it acts on 401
      // and nothing else.
      // simpleFetch resolves rather than rejects on a network failure, handing
      // back the Error itself, so catching is not enough -- anything without a
      // numeric status is normalised to one shape here.
      const settle = (promise) =>
        promise
          .catch((err) => err)
          .then((res) =>
            res && typeof res.status === "number"
              ? res
              : { status: 0, body: res }
          );
      return Promise.all([
        settle(
          simpleFetch("/donors/status", "GET", {}, false, {
            isCustomDomain: parsedDomain.isCustomDomain,
          })
        ),
        settle(simpleFetch("/organizations/status", "GET")),
        settle(simpleFetch("/organizations", "GET")),
      ]).then(([donorRes, orgRes, orgData]) => {
        if (donorRes.status === 204) {
          this.setLoginStatus(true);
          this.setOrgLoginStatus(false);
          this.setAdminStatus(false);
          return { status: 204, body: {}, kind: "donor" };
        }
        // Not a donor session — the same cookie may still be an organization.
        if (orgRes.status !== 204) {
          return failed(orgRes);
        }
        this.setOrgLoginStatus(true);
        this.setLoginStatus(false);
        this.setAdminStatus(false);
        // Which kind of organisation decides which workspace opens, so it is
        // read from the organisation itself rather than trusted from the
        // sign-in response alone. If that read failed, the login response is
        // the fallback, and a nonprofit is the safer default of the two — it
        // is the account type that cannot be handed a business's tools by
        // mistake.
        const organizationType =
          orgData.status === 200 && orgData.body && orgData.body.organizationObj
            ? orgData.body.organizationObj.organizationType
            : loginOrgType;
        const kind = organizationType === "business" ? "business" : "nonprofit";
        // Recorded before resolving so that whoever handles the sign-in reads
        // the same workspace a later refresh will.
        this.setOrgKind(kind);
        return { status: 204, body: {}, kind };
      });
    });
  }

  logout() {
    return simpleFetch("/donors/logout", "GET", {}).then((res) => {
      this.setLoginStatus(false);
      this.setOrgLoginStatus(false);
      this.setAdminStatus(false);
      return res;
    });
  }

  /**
   * Sign out on this side only: the same three flags logout() clears, with no
   * request to the server. For a sign-out that has to hold even when the
   * logout call cannot be made -- Plug's idle timeout, where the person has
   * walked away and a failed request must not leave the stored session valid
   * for whoever sits down next (CP-377).
   */
  forgetSession() {
    this.setLoginStatus(false);
    this.setOrgLoginStatus(false);
    this.setAdminStatus(false);
  }

  deleteAccount(credentials) {
    const parsedDomain = getOrganizationID(window.location.href);
    return simpleFetch("/donors", "DELETE", credentials, false, {
      isCustomDomain: parsedDomain.isCustomDomain,
    }).then((res) => {
      if ([200, 204].includes(res.status)) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("expiredIn");
        this.setLoginStatus(false);
        this.setOrgLoginStatus(false);
        this.setAdminStatus(false);
      }
      return res;
    });
  }

  setUserStatus(name, status) {
    const object = { status: status, timestamp: new Date().getTime() };
    localStorage.setItem(name, JSON.stringify(object));
    this.setState({ isAuth: status });
  }

  setLoginStatus(status) {
    this.setUserStatus(DONOR_AUTH, status);
  }

  setAdminStatus(status) {
    this.setUserStatus(ADMIN_AUTH, status);
  }

  setOrgLoginStatus(status) {
    this.setUserStatus(ORG_AUTH, status);
    // The kind of organisation only means anything while an organisation is
    // signed in. Clearing it here covers every way a session ends — logout, a
    // rejected sign-in, a donor signing in over it — so the next organisation
    // cannot inherit the last one's workspace.
    if (!status) {
      this.setOrgKind(null);
    }
  }

  /**
   * Records which workspace an organisation session opens. Kept next to
   * ORG_AUTH rather than in component state so that a refresh lands in the
   * same workspace the sign-in chose, instead of defaulting to nonprofit.
   */
  setOrgKind(kind) {
    try {
      if (kind === "business" || kind === "nonprofit") {
        localStorage.setItem(ORG_KIND, kind);
      } else {
        localStorage.removeItem(ORG_KIND);
      }
    } catch (e) {
      // Storage unavailable — the workspace falls back to nonprofit, which is
      // the account type that cannot be handed a business's tools by mistake.
    }
  }

  getAdminLoginStatus() {
    return checkLoginStatus(ADMIN_AUTH);
  }

  getOrgLoginStatus() {
    return checkLoginStatus(ORG_AUTH);
  }

  getDonorLoginStatus() {
    return checkLoginStatus(DONOR_AUTH);
  }

  render() {
    return (
      <Translation>
        {(t, { i18n }) => (
          <AuthContext.Provider
            value={{
              t: t,
              i18n: i18n,
              language: i18n.language ? i18n.language : DEFAULT_LANGUAGE,
              isAuth: this.getDonorLoginStatus(),
              isAdminAuth: this.getAdminLoginStatus(),
              isOrgAuth: this.getOrgLoginStatus(),
              login: this.login,
              orgLogin: this.orgLogin,
              adminLogin: this.adminLogin,
              marketplaceLogin: this.marketplaceLogin,
              logout: this.logout,
              orgLogout: this.logout,
              forgetSession: this.forgetSession,
              deleteAccount: this.deleteAccount,
              appPlatform: appPlatform, // Expose the app platform
            }}
          >
            {this.props.children}
          </AuthContext.Provider>
        )}
      </Translation>
    );
  }
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export { AuthContext, AuthProvider, AuthConsumer };
